/**
 * Block 9 & 10: People Rate Distribution Section (利用人数割合)
 * 
 * Combined section with three charts:
 * - Upper: Horizontal stacked bar chart by room
 * - Lower-left: Stacked area chart (monthly trend)
 * - Lower-right: Pie/Donut chart (people count distribution)
 * 
 * Data sources:
 * - meeting_room_mart_people_rates (upper and lower-left charts)
 * - meeting_room_mart_people_counts (lower-right chart)
 */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { QUERY_STRING_STORE } from '@/helpers/constants';
import { Dropdown } from 'primereact/dropdown';
import { Paginator } from 'primereact/paginator';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import LoadingSpinner from '@/components/CustomComponent/LoadingSpinner';
import {
  fetchMeetingRoomMartPeopleRates,
  fetchMeetingRoomMartPeopleCounts,
  getLastNMonths,
  formatMonthShort,
} from './query-request';
import './PeopleRateChart.less';

// Rate keys for iteration
const RATE_KEYS = ['rate_1', 'rate_2', 'rate_3', 'rate_4', 'rate_5'];

// Colors for rate categories
const RATE_COLORS = {
  rate_1: '#2980b9',  // Dark blue (<=25%)
  rate_2: '#7ec8e3',  // Light blue (<=50%) - more distinct
  rate_3: '#f5a623',  // Orange (<=75%)
  rate_4: '#d35400',  // Dark orange (<=100%)
  rate_5: '#c0392b',  // Red (100%<)
};

// Colors for pie chart (count categories)
const PIE_COLORS = [
  '#2980b9',
  '#A2C7EC',
  '#7ec8e3',
  '#F1F8BE',
  '#f5a623',
  '#EE8A12',
  '#d35400',
  '#BA1D0E',
  '#c0392b',
  '#5C0000'
];

// Count category labels
const COUNT_LABELS = {
  1: '1人',
  2: '2人',
  3: '3人',
  4: '4人',
  5: '5人',
  6: '6人',
  7: '7人',
  8: '8人',
  9: '9人',
  10: '10人以上',
};

@connect(state => ({
  querystring: state.querystring,
}))
class PeopleRateChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      // Upper chart state
      barChartData: [],
      selectedMonth: null,
      availableMonths: [],
      sortField: 'room_name',
      sortDirection: 'asc',
      first: 0,
      rows: 5,
      hoveredBarRow: null,
      // Lower-left chart state
      areaChartData: [],
      selectedRoomForArea: null,
      availableRoomsForArea: [],
      // Lower-right chart state
      pieChartData: [],
      selectedRoomForPie: null,
      selectedMonthForPie: null,
      availableRoomsForPie: [],
      availableMonthsForPie: [],
      // Raw data
      rawRatesData: [],
      rawCountsData: [],
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps) {
    const prevBranch = prevProps?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_BRANCH]?.[0];
    const currentBranch = this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_BRANCH]?.[0];
    if (prevBranch !== currentBranch) {
      this.fetchData();
    }
  }

  fetchData = async () => {
    const tenantId = this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0];
    const branchId = this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_BRANCH]?.[0];

    if (!tenantId) {
      this.setState({ barChartData: [], areaChartData: [], pieChartData: [] });
      return;
    }

    this.setState({ isLoading: true });

    try {
      const months = getLastNMonths(4);

      // Fetch both datasets in parallel
      const [ratesData, countsData] = await Promise.all([
        fetchMeetingRoomMartPeopleRates({ tenantId, branchId, months }),
        fetchMeetingRoomMartPeopleCounts({ tenantId, branchId, months }),
      ]);

      // Process rates data
      const uniqueMonths = [...new Set((ratesData || []).map(d => d.month))].sort().reverse();
      const uniqueRoomsForArea = [...new Set((ratesData || []).map(d => d.room_name))].filter(Boolean).sort();

      const selectedMonth = this.state.selectedMonth || uniqueMonths[0];
      const selectedRoomForArea = this.state.selectedRoomForArea || uniqueRoomsForArea[0];

      // Process counts data
      const uniqueRoomsForPie = [...new Set((countsData || []).map(d => d.room_name))].filter(Boolean).sort();
      const uniqueMonthsForPie = [...new Set((countsData || []).map(d => d.month))].sort().reverse();

      const selectedRoomForPie = this.state.selectedRoomForPie || uniqueRoomsForPie[0];
      const selectedMonthForPie = this.state.selectedMonthForPie || uniqueMonthsForPie[0];

      this.setState({
        rawRatesData: ratesData || [],
        rawCountsData: countsData || [],
        availableMonths: uniqueMonths.map(m => ({ label: formatMonthShort(m), value: m })),
        availableRoomsForArea: uniqueRoomsForArea.map(r => ({ label: r, value: r })),
        availableRoomsForPie: uniqueRoomsForPie.map(r => ({ label: r, value: r })),
        availableMonthsForPie: uniqueMonthsForPie.map(m => ({ label: formatMonthShort(m), value: m })),
        selectedMonth,
        selectedRoomForArea,
        selectedRoomForPie,
        selectedMonthForPie,
        isLoading: false,
      }, () => {
        this.updateBarChartData();
        this.updateAreaChartData();
        this.updatePieChartData();
      });
    } catch (error) {
      console.error('Error fetching people rates data:', error);
      this.setState({ barChartData: [], areaChartData: [], pieChartData: [], isLoading: false });
    }
  };

  updateBarChartData = () => {
    const { rawRatesData, selectedMonth, sortField, sortDirection } = this.state;

    // Filter by selected month
    const monthData = rawRatesData.filter(d => d.month === selectedMonth);

    // Transform data
    let barChartData = monthData.map(d => ({
      room_name: d.room_name || 'Unknown',
      device_identifier: d.device_identifier,
      rate_1: Math.round((d.rate_1 || 0) * 100),
      rate_2: Math.round((d.rate_2 || 0) * 100),
      rate_3: Math.round((d.rate_3 || 0) * 100),
      rate_4: Math.round((d.rate_4 || 0) * 100),
      rate_5: Math.round((d.rate_5 || 0) * 100),
    }));

    // Sort
    barChartData.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      const result = typeof aVal === 'string' ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortDirection === 'asc' ? result : -result;
    });

    this.setState({ barChartData });
  };

  updateAreaChartData = () => {
    const { rawRatesData, selectedRoomForArea } = this.state;

    // Filter by selected room
    const roomData = rawRatesData
      .filter(d => d.room_name === selectedRoomForArea)
      .sort((a, b) => a.month.localeCompare(b.month));

    // Transform data
    const areaChartData = roomData.map(d => ({
      month: formatMonthShort(d.month),
      monthFull: d.month,
      rate_1: Math.round((d.rate_1 || 0) * 100),
      rate_2: Math.round((d.rate_2 || 0) * 100),
      rate_3: Math.round((d.rate_3 || 0) * 100),
      rate_4: Math.round((d.rate_4 || 0) * 100),
      rate_5: Math.round((d.rate_5 || 0) * 100),
    }));

    this.setState({ areaChartData });
  };

  updatePieChartData = () => {
    const { rawCountsData, selectedRoomForPie, selectedMonthForPie } = this.state;

    // Filter by selected room and month
    const filteredData = rawCountsData.filter(
      d => d.room_name === selectedRoomForPie && d.month === selectedMonthForPie
    );

    // Transform data - sort by count_category ascending
    const pieChartData = filteredData
      .map(d => ({
        name: COUNT_LABELS[d.count_category] || `${d.count_category}人`,
        value: Math.round((d.rate || 0) * 100),
        count_category: d.count_category,
      }))
      .sort((a, b) => a.count_category - b.count_category);

    this.setState({ pieChartData });
  };

  handleMonthChange = (e) => {
    this.setState({ selectedMonth: e.value, first: 0 }, () => this.updateBarChartData());
  };

  handleRoomForAreaChange = (e) => {
    this.setState({ selectedRoomForArea: e.value }, () => this.updateAreaChartData());
  };

  handleRoomForPieChange = (e) => {
    this.setState({ selectedRoomForPie: e.value }, () => this.updatePieChartData());
  };

  handleMonthForPieChange = (e) => {
    this.setState({ selectedMonthForPie: e.value }, () => this.updatePieChartData());
  };

  handleSort = (field) => {
    this.setState(prevState => {
      const newDirection = prevState.sortField === field && prevState.sortDirection === 'asc' ? 'desc' : 'asc';
      return { sortField: field, sortDirection: newDirection, first: 0 };
    }, () => this.updateBarChartData());
  };

  handlePageChange = (e) => {
    this.setState({ first: e.first, rows: e.rows });
  };

  getSortIconSrc = (field) => {
    const { sortField, sortDirection } = this.state;
    if (sortField !== field) return '/assets/img/sort-icon.svg';
    if (sortDirection === 'asc') return '/assets/img/sort-up-icon.svg';
    return '/assets/img/sort-down-icon.svg';
  };

  formatMonthLabel = (month) => {
    if (!month) return '';
    const [year, m] = month.split('-');
    return `${year}年${parseInt(m)}月`;
  };

  // Custom tooltip for bar chart
  renderBarTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0]?.payload;
    if (!data) return null;

    return (
      <div className="custom-tooltip">
        <div className="tooltip-title">{data.room_name}</div>
        <div className="tooltip-row rate-5-row">
          <span className="tooltip-label">100%&lt;</span>
          <span className="tooltip-value">{data.rate_5}%</span>
        </div>
        <div className="tooltip-row rate-4-row">
          <span className="tooltip-label">&lt;=100%</span>
          <span className="tooltip-value">{data.rate_4}%</span>
        </div>
        <div className="tooltip-row rate-3-row">
          <span className="tooltip-label">&lt;=75%</span>
          <span className="tooltip-value">{data.rate_3}%</span>
        </div>
        <div className="tooltip-row rate-2-row">
          <span className="tooltip-label">&lt;=50%</span>
          <span className="tooltip-value">{data.rate_2}%</span>
        </div>
        <div className="tooltip-row rate-1-row">
          <span className="tooltip-label">&lt;=25%</span>
          <span className="tooltip-value">{data.rate_1}%</span>
        </div>
      </div>
    );
  };

  // Custom tooltip for area chart
  renderAreaTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0]?.payload;
    if (!data) return null;

    // Get monthFull from data, or reconstruct from month if needed
    const monthFull = data.monthFull || data.month;
    const monthLabel = monthFull && monthFull.includes('-')
      ? this.formatMonthLabel(monthFull)
      : data.month || '';

    return (
      <div className="custom-tooltip">
        <div className="tooltip-title">{monthLabel}</div>
        <div className="tooltip-row rate-5-row">
          <span className="tooltip-label">100%&lt;</span>
          <span className="tooltip-value">{data.rate_5}%</span>
        </div>
        <div className="tooltip-row rate-4-row">
          <span className="tooltip-label">&lt;=100%</span>
          <span className="tooltip-value">{data.rate_4}%</span>
        </div>
        <div className="tooltip-row rate-3-row">
          <span className="tooltip-label">&lt;=75%</span>
          <span className="tooltip-value">{data.rate_3}%</span>
        </div>
        <div className="tooltip-row rate-2-row">
          <span className="tooltip-label">&lt;=50%</span>
          <span className="tooltip-value">{data.rate_2}%</span>
        </div>
        <div className="tooltip-row rate-1-row">
          <span className="tooltip-label">&lt;=25%</span>
          <span className="tooltip-value">{data.rate_1}%</span>
        </div>
      </div>
    );
  };

  // Custom tooltip for pie chart
  renderPieTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0]?.payload;
    if (!data) return null;

    return (
      <div className="custom-tooltip pie-tooltip">
        <div className="tooltip-title">{data.name}：{data.value}％</div>
      </div>
    );
  };

  renderBarChart = () => {
    const { barChartData, first, rows, hoveredBarRow } = this.state;
    const paginatedData = barChartData.slice(first, first + rows);

    if (paginatedData.length === 0) {
      return <div className="no-data-container">データがありません</div>;
    }

    return (
      <div className="bar-chart-container">
        {/* Table Header */}
        <div className="table-header">
          <div
            className="header-cell name-col sortable"
            onClick={() => this.handleSort('room_name')}
          >
            部屋名
            <button className="sort-button">
              <img
                className="custom-sort-icon"
                src={this.getSortIconSrc('room_name')}
                alt="sort"
              />
            </button>
          </div>
        </div>

        {/* Table Body - HTML-based bars */}
        <div className="table-body">
          {paginatedData.map((entry, rowIndex) => (
            <div
              key={entry.room_name}
              className="table-row"
              onMouseEnter={() => this.setState({ hoveredBarRow: entry.room_name })}
              onMouseLeave={() => this.setState({ hoveredBarRow: null })}
            >
              <div className="cell name-cell">{entry.room_name}</div>
              <div className="cell bar-cell">
                <div className="stacked-bar">
                  {RATE_KEYS.map(key => {
                    const value = entry[key] || 0;
                    return (
                      <div
                        key={key}
                        className="bar-segment"
                        style={{
                          width: `${value}%`,
                          backgroundColor: RATE_COLORS[key],
                        }}
                      />
                    );
                  })}
                </div>
              </div>
              {/* Tooltip */}
              {hoveredBarRow === entry.room_name && (
                <div className="custom-tooltip bar-tooltip">
                  <div className="tooltip-title">{entry.room_name}</div>
                  <div className="tooltip-row rate-5-row">
                    <span className="tooltip-label">100%&lt;</span>
                    <span className="tooltip-value">{entry.rate_5}%</span>
                  </div>
                  <div className="tooltip-row rate-4-row">
                    <span className="tooltip-label">&lt;=100%</span>
                    <span className="tooltip-value">{entry.rate_4}%</span>
                  </div>
                  <div className="tooltip-row rate-3-row">
                    <span className="tooltip-label">&lt;=75%</span>
                    <span className="tooltip-value">{entry.rate_3}%</span>
                  </div>
                  <div className="tooltip-row rate-2-row">
                    <span className="tooltip-label">&lt;=50%</span>
                    <span className="tooltip-value">{entry.rate_2}%</span>
                  </div>
                  <div className="tooltip-row rate-1-row">
                    <span className="tooltip-label">&lt;=25%</span>
                    <span className="tooltip-value">{entry.rate_1}%</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* X-axis labels */}
        <div className="x-axis">
          <span className="x-label">1%</span>
          <span className="x-label">20%</span>
          <span className="x-label">40%</span>
          <span className="x-label">60%</span>
          <span className="x-label">80%</span>
          <span className="x-label">100%</span>
        </div>

        {/* Pagination - always show */}
        <Paginator
          first={first}
          rows={rows}
          totalRecords={barChartData.length}
          onPageChange={this.handlePageChange}
          className="custom-paginator"
          template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
        />
      </div>
    );
  };

  renderAreaChart = () => {
    const { areaChartData } = this.state;

    if (areaChartData.length === 0) {
      return <div className="no-data-container">データがありません</div>;
    }

    return (
      <ResponsiveContainer width="100%" height={250}>
        <AreaChart data={areaChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#3a4570" />
          <XAxis dataKey="month" stroke="#a2a8bb" />
          <YAxis
            stroke="#a2a8bb"
            domain={[0, 100]}
            tickFormatter={(v) => v === 0 ? '(%)' : v}
            ticks={[0, 30, 60, 90]}
          />
          <Tooltip content={this.renderAreaTooltip} />
          {RATE_KEYS.map(key => (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              stackId="1"
              stroke={RATE_COLORS[key]}
              fill={RATE_COLORS[key]}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  renderPieChart = () => {
    const { pieChartData } = this.state;

    if (pieChartData.length === 0) {
      return <div className="no-data-container">データがありません</div>;
    }

    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={pieChartData}
            cx="50%"
            cy="50%"
            innerRadius={70}
            outerRadius={120}
            fill="#8884d8"
            dataKey="value"
            startAngle={90}
            endAngle={-270}
          >
            {pieChartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={PIE_COLORS[index % PIE_COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip content={this.renderPieTooltip} />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  render() {
    const {
      isLoading,
      availableMonths,
      selectedMonth,
      availableRoomsForArea,
      selectedRoomForArea,
      availableRoomsForPie,
      selectedRoomForPie,
      availableMonthsForPie,
      selectedMonthForPie,
    } = this.state;

    return (
      <div className="people-rate-chart-wrapper">
        {/* Upper Section: Bar Chart with title */}
        <div className="people-rate-chart upper-section">
          <div className="section-header">
            <h3 className="section-title">利用人数割合</h3>
            <div className="section-controls">
              <Dropdown
                value={selectedMonth}
                options={availableMonths}
                onChange={this.handleMonthChange}
                placeholder="月を選択"
                className="month-dropdown"
              />
            </div>
          </div>

          {isLoading ? (
            <LoadingSpinner />
          ) : (
            this.renderBarChart()
          )}
        </div>

        {/* Lower Charts Row - separate sections without titles */}
        <div className="lower-charts-row">
          {/* Lower-Left: Area Chart */}
          <div className="people-rate-chart lower-left-section">
            <div className="chart-header">
              <Dropdown
                value={selectedRoomForArea}
                options={availableRoomsForArea}
                onChange={this.handleRoomForAreaChange}
                placeholder="部屋を選択"
                className="room-dropdown"
              />
            </div>
            {isLoading ? <LoadingSpinner /> : this.renderAreaChart()}
          </div>

          {/* Lower-Right: Pie Chart */}
          <div className="people-rate-chart lower-right-section">
            <div className="pie-chart-container">
              <div className="pie-chart-dropdowns">
                <Dropdown
                  value={selectedRoomForPie}
                  options={availableRoomsForPie}
                  onChange={this.handleRoomForPieChange}
                  placeholder="部屋を選択"
                  className="room-dropdown"
                />
                <Dropdown
                  value={selectedMonthForPie}
                  options={availableMonthsForPie}
                  onChange={this.handleMonthForPieChange}
                  placeholder="月を選択"
                  className="month-dropdown"
                />
              </div>
              {isLoading ? <LoadingSpinner /> : this.renderPieChart()}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default PeopleRateChart;
